// Shared validation: no restaurant content or customer health data is inferred.
const { categoryKey, menuCategories, visibleMenu } = require('./menuStructure');
const { ALLERGENS } = require('./menuAllergens');
const PREFERENCES = { outdoor: 'Tavolo all’aperto', highchair: 'Seggiolone', step_free: 'Accesso senza gradini', birthday: 'Compleanno', quiet: 'Zona tranquilla' };
const INFO_FIELDS = { address: 'Indirizzo', parking: 'Parcheggio', accessibility: 'Accessibilità', children: 'Bambini e seggioloni', pets: 'Animali', payments: 'Pagamenti', other: 'Altre informazioni utili' };
const DIETARY_FIELDS = {
  celiac: 'Celiachia e preparazioni senza glutine',
  foodAllergies: 'Allergie alimentari',
  intolerances: 'Intolleranze alimentari',
  crossContact: 'Preparazione e rischio di contaminazione',
  staffContact: 'Referente e modalità di contatto',
  advanceNotice: 'Preavviso e limiti del servizio',
};
const EMPTY_CONTENT = { info: {}, menu: [], fixedMenus: [], dietary: { verified: false } };
const ALLERGY_NOTICE_VERSION = 'guest-2026-10-06';
function invalid(message) { return Object.assign(new Error(message), { status: 400 }); }
function clean(value, max, required = false) {
  if (typeof value !== 'string' || value.trim().length > max || [...value].some((char) => char.charCodeAt(0) < 32 && !['\t','\n','\r'].includes(char))) throw invalid('Campo non valido o troppo lungo.');
  const result = value.trim();
  if (required && !result) throw invalid('Compila i campi obbligatori.');
  return result;
}
function options(values, dictionary) {
  if (!Array.isArray(values) || values.length > Object.keys(dictionary).length || values.some((v) => typeof v !== 'string' || !Object.hasOwn(dictionary, v))) throw invalid('Selezione non valida.');
  return [...new Set(values)];
}
function validateDietary(value = {}, publish = false, demo = false) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw invalid('Scheda alimentare non valida.');
  const fields = Object.fromEntries(Object.keys(DIETARY_FIELDS).map((key) => [key, clean(value[key] ?? '', 600)]));
  const hasContent = Object.values(fields).some(Boolean);
  if (publish && !demo && hasContent) {
    if (value.verified !== true) throw invalid('Verifica la scheda su allergie, intolleranze e celiachia con il responsabile del ristorante prima di pubblicarla.');
    if (!fields.crossContact || !fields.staffContact) throw invalid('Specifica preparazione e rischio di contaminazione, referente e modalità di contatto prima di pubblicare la scheda alimentare.');
  }
  return { ...fields, verified: hasContent && !demo && value.verified === true };
}
function publishedDietary(content) {
  // Old, demo or incomplete records must never become evidence of suitability.
  try {
    if (content?.demo === true || content?.dietary?.verified !== true) return { verified: false };
    return validateDietary(content.dietary, true);
  } catch { return { verified: false }; }
}
function validatePrice(value, required = false) {
  if (value != null && !['number','string'].includes(typeof value)) throw invalid('Prezzo non valido.');
  const price = (typeof value === 'string' && !value.trim()) || value == null ? null : Number(value);
  if ((required && price === null) || (price !== null && (!Number.isFinite(price) || price < 0 || price > 10000 || Math.abs(price * 100 - Math.round(price * 100)) > 0.000001))) throw invalid('Inserisci un prezzo valido con massimo due decimali.');
  return price;
}
function validateVariants(value = []) {
  if (!Array.isArray(value) || value.length > 20 || value.length === 1) throw invalid('Inserisci da 2 a 20 formati, oppure usa un prezzo unico.');
  const names = new Set();
  return value.map(variant => {
    if (!variant || typeof variant !== 'object') throw invalid('Formato non valido.');
    const label = clean(variant.label, 40, true), key = categoryKey(label);
    if (names.has(key)) throw invalid('Ogni formato deve avere un nome diverso.');
    names.add(key);
    return { label, price: validatePrice(variant.price, true) };
  });
}
function validateDish(item, publish = false, demo = false) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) throw invalid('Piatto non valido.');
    const variants = validateVariants(item.variants);
    const price = variants.length ? null : validatePrice(item.price);
    if (publish && !demo && item.verified !== true) throw invalid('Verifica ingredienti, prezzo e allergeni di ogni piatto prima di pubblicare.');
    if (publish && !demo && item.allergenStatus !== 'declared') throw invalid(`Controlla e conferma gli allergeni di ${clean(item.name,100,true)} prima di pubblicare.`);
    return { name: clean(item.name, 100, true), category: clean(item.category || '', 60), description: clean(item.description || '', 500), ingredients: clean(item.ingredients || '', 500), price,
      allergens: demo ? [] : options(item.allergens || [], ALLERGENS), allergenStatus: !demo && item.allergenStatus === 'declared' ? 'declared' : 'unknown', frozen: item.frozen === true, available: item.available !== false, verified: !demo && item.verified === true, ...(variants.length ? { variants } : {}),
      ...(item.allergenSource ? { allergenSource: clean(item.allergenSource,500), allergenSuggestions: demo ? [] : options(item.allergenSuggestions || [],ALLERGENS) } : {}) };
}
function validateFixedMenus(value = [], publish = false, demo = false) {
  if (!Array.isArray(value) || value.length > 20) throw invalid('Puoi inserire al massimo 20 menù fissi.');
  let dishes = 0;
  return value.map(fixed => {
    if (!fixed || typeof fixed !== 'object' || Array.isArray(fixed)) throw invalid('Menù fisso non valido.');
    const name = clean(fixed.name, 100, true);
    if (!Array.isArray(fixed.courses) || fixed.courses.length > 10 || (publish && !fixed.courses.length)) throw invalid(`Aggiungi da 1 a 10 portate a ${name}.`);
    const courses = fixed.courses.map(course => {
      if (!course || !['all','one'].includes(course.choice) || !Array.isArray(course.dishes) || course.dishes.length > 10 || (publish && !course.dishes.length)) throw invalid(`Completa le portate di ${name}: scegli i piatti inclusi.`);
      dishes += course.dishes.length;
      if (dishes > 150) throw invalid('I menù fissi possono contenere al massimo 150 piatti in totale.');
      return { name: clean(course.name, 60, true), choice: course.choice, dishes: course.dishes.map(item => {
        // A package has its own price: do not copy à-la-carte prices or formats into it.
        const dish = validateDish({ ...item, price: null, variants: [], category: '', available: true }, publish, demo);
        return dish;
      }) };
    });
    return { name, price: validatePrice(fixed.price, publish), description: clean(fixed.description ?? '', 500),
      included: clean(fixed.included ?? '', 300), excluded: clean(fixed.excluded ?? '', 300),
      availability: clean(fixed.availability ?? '', 200), available: fixed.available !== false, courses };
  });
}
function validateContent(value, publish = false) {
  if (!value || !Array.isArray(value.menu) || value.menu.length > 150) throw invalid('Il menù può contenere al massimo 150 piatti.');
  const demo = value.demo === true;
  const info = Object.fromEntries(Object.keys(INFO_FIELDS).map((key) => [key, clean(value.info?.[key] || '', 600)]));
  const menu = value.menu.map(item => validateDish(item, publish, demo));
  const fixedMenus = validateFixedMenus(value.fixedMenus, publish, demo);
  if (value.categories != null && (!Array.isArray(value.categories) || value.categories.length > 150)) throw invalid('Categorie non valide.');
  const seen = new Set();
  const explicit = (value.categories || []).map(category => {
    if (!category || typeof category !== 'object' || (category.visible != null && typeof category.visible !== 'boolean')) throw invalid('Categoria non valida.');
    const name = clean(category.name, 60, true), key = categoryKey(name);
    if (seen.has(key)) throw invalid('Esiste già una categoria con questo nome.');
    seen.add(key); return { name, visible: category.visible !== false };
  });
  const categories = menuCategories({ categories: explicit, menu });
  if (categories.length > 150) throw invalid('Il menù può contenere al massimo 150 categorie.');
  const dietary = validateDietary(value.dietary, publish, demo);
  return { info, menu, fixedMenus, dietary, categories, ...(demo ? { demo: true } : {}) };
}
function validateRequests(value = {}) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw invalid('Segnalazione non valida.');
  const preferences = options(value.preferences || [], PREFERENCES);
  const allergies = options(value.allergies || [], ALLERGENS);
  const allergyNote = clean(value.allergyNote || '', 300);
  if ((allergies.length || allergyNote) && value.allergyConsent !== true) throw invalid('Serve il consenso esplicito per registrare la segnalazione alimentare. Puoi anche parlarne direttamente con il ristorante.');
  return { preferences, allergies, allergyNote, allergyConsent: (allergies.length > 0 || Boolean(allergyNote)) && value.allergyConsent === true, noticeVersion: ALLERGY_NOTICE_VERSION };
}
function selectMenu(content, query = '', limit = 3) {
  const terms = String(query).toLocaleLowerCase('it').match(/[\p{L}\p{N}]{3,}/gu) || [];
  const items = visibleMenu(content).filter((item) => item.verified || content.demo === true);
  if (!terms.length) return []; // Ask what interests the caller, never read the menu.
  return items.map((item, index) => ({ item, index, score: terms.reduce((n, term) => n + Number([item.name, item.category, item.description, item.ingredients, ...(item.variants || []).map(v => v.label), ...(item.allergens || []).map((a) => ALLERGENS[a])].join(' ').toLocaleLowerCase('it').includes(term)), 0) }))
    .filter((match) => match.score > 0).sort((a, b) => b.score - a.score || a.index - b.index).slice(0, limit).map(({ item }) => item);
}
module.exports = { ALLERGENS, PREFERENCES, INFO_FIELDS, DIETARY_FIELDS, EMPTY_CONTENT, ALLERGY_NOTICE_VERSION, clean, validateContent, validateDietary, publishedDietary, validateRequests, selectMenu, validatePrice, validateVariants, validateFixedMenus };
